import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Link,
  Chip,
  IconButton
} from '@mui/material';
import { 
  Search, 
  Add, 
  Person, 
  Edit, 
  Phone as PhoneIcon, 
  Email as EmailIcon 
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { customerAPI } from '../../services/api';
import AppHeader from '../../components/AppHeader';

// Helper functions
const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .split(/(\s+|-)/g)
    .map((part) => {
      if (part === ' ' || part === '-') return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
};

const formatPhoneNumber = (text) => {
  if (!text) return '';
  let cleaned = text.trim().replace(/[^\d+]/g, '');
  
  // Live-Formatierung: sobald "0" eingegeben wird → "+49"
  if (cleaned.startsWith('0')) {
    cleaned = '+49' + cleaned.substring(1);
  }
  else if (cleaned && !cleaned.startsWith('+')) {
    cleaned = '+49' + cleaned;
  }
  
  return cleaned;
};

const validateKundennummer = (value) => {
  const cleaned = value.replace(/\D/g, '');
  return cleaned.length === 5;
};

const formatKundennummer = (value) => {
  return value.replace(/\D/g, '').slice(0, 5);
};

export default function CustomerSearchPageNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const isWP = location.pathname.includes('wp');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Neuanlage Dialog
  const [newCustomerDialog, setNewCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    ku_nr: '',
    name: '',
    strasse: '',
    plz: '',
    ort: '',
    email: '',
    mobilfunk: '',
    festnetz: ''
  });
  const [newCustomerErrors, setNewCustomerErrors] = useState({});
  
  // Bearbeiten Dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [editErrors, setEditErrors] = useState({});

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Bitte Suchbegriff eingeben');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await customerAPI.search(searchQuery);
      setCustomers(response.customers || []);
      if (!response.customers || response.customers.length === 0) {
        setError('Keine Kunden gefunden');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectCustomer = (customer) => {
    const targetPath = '/protocol/ibn-wp-form';
    navigate(targetPath, { state: { customer } });
  };

  const handleOpenNewCustomer = () => {
    setNewCustomer({
      ku_nr: '',
      name: '',
      strasse: '',
      plz: '',
      ort: '',
      email: '',
      mobilfunk: '',
      festnetz: ''
    });
    setNewCustomerErrors({});
    setNewCustomerDialog(true);
  };

  const handleNewCustomerChange = (field, value) => {
    let processedValue = value;
    
    if (field === 'ku_nr') {
      processedValue = formatKundennummer(value);
    } else if (field === 'name' || field === 'strasse' || field === 'ort') {
      processedValue = capitalizeWords(value);
    } else if (field === 'mobilfunk' || field === 'festnetz') {
      // Live-Formatierung für Telefonnummern
      processedValue = formatPhoneNumber(value);
    }
    
    setNewCustomer(prev => ({ ...prev, [field]: processedValue }));
    
    // Clear error for this field
    if (newCustomerErrors[field]) {
      setNewCustomerErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateNewCustomer = () => {
    const errors = {};
    
    if (!newCustomer.ku_nr || !validateKundennummer(newCustomer.ku_nr)) {
      errors.ku_nr = 'Genau 5 Ziffern erforderlich';
    }
    
    if (!newCustomer.name || newCustomer.name.trim() === '') {
      errors.name = 'Name ist erforderlich';
    }
    
    setNewCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCustomer = async () => {
    if (!validateNewCustomer()) {
      return;
    }

    try {
      const customerData = {
        ...newCustomer,
        mobilfunk: newCustomer.mobilfunk ? formatPhoneNumber(newCustomer.mobilfunk) : '',
        festnetz: newCustomer.festnetz ? formatPhoneNumber(newCustomer.festnetz) : ''
      };
      
      await customerAPI.create(customerData);
      setNewCustomerDialog(false);
      
      // Re-search to show the new customer
      if (newCustomer.name) {
        setSearchQuery(newCustomer.name);
        setTimeout(() => handleSearch(), 500);
      }
    } catch (err) {
      console.error('Create customer error:', err);
      setError('Fehler beim Anlegen des Kunden');
    }
  };

  const handleOpenEdit = (customer) => {
    setEditCustomer({...customer});
    setEditErrors({});
    setEditDialog(true);
  };

  const handleEditChange = (field, value) => {
    let processedValue = value;
    
    if (field === 'name' || field === 'strasse' || field === 'ort') {
      processedValue = capitalizeWords(value);
    } else if (field === 'mobilfunk' || field === 'festnetz') {
      processedValue = formatPhoneNumber(value);
    }
    
    setEditCustomer(prev => ({ ...prev, [field]: processedValue }));
    
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEdit = () => {
    const errors = {};
    
    if (!editCustomer.name || editCustomer.name.trim() === '') {
      errors.name = 'Name ist erforderlich';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!validateEdit()) {
      return;
    }

    try {
      const updatedData = {
        ...editCustomer,
        mobilfunk: editCustomer.mobilfunk ? formatPhoneNumber(editCustomer.mobilfunk) : '',
        festnetz: editCustomer.festnetz ? formatPhoneNumber(editCustomer.festnetz) : ''
      };
      
      await customerAPI.update(updatedData);
      setEditDialog(false);
      
      // Update local customer list
      setCustomers(customers.map(c => 
        c.ku_nr === editCustomer.ku_nr ? updatedData : c
      ));
    } catch (err) {
      console.error('Update customer error:', err);
      setError('Fehler beim Aktualisieren des Kunden');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader />

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Kunde suchen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Suchen Sie nach Kundennummer oder Name
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              label="Kundennummer oder Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="z.B. 12345 oder Müller"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 120 }}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            >
              SUCHEN
            </Button>
          </Box>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<Add />}
            onClick={handleOpenNewCustomer}
            sx={{ mb: 2 }}
          >
            + NEUEN KUNDEN ANLEGEN
          </Button>

          {error && customers.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {customers.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Suchergebnisse ({customers.length})
              </Typography>
              <List>
                {customers.map((customer, index) => (
                  <ListItem 
                    key={index} 
                    disablePadding
                    sx={{ 
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: '#fafafa'
                    }}
                  >
                    <ListItemButton onClick={() => handleSelectCustomer(customer)}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                        <Person sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">
                            {customer.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {customer.ku_nr && `Kd.-Nr.: ${customer.ku_nr}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {customer.strasse && `${customer.strasse}, `}
                            {customer.plz && `${customer.plz} `}
                            {customer.ort}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {customer.email && (
                              <Link
                                href={`mailto:${customer.email}`}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Chip
                                  icon={<EmailIcon />}
                                  label={customer.email}
                                  size="small"
                                  clickable
                                  color="primary"
                                  variant="outlined"
                                />
                              </Link>
                            )}
                            {customer.mobilfunk && (
                              <Link
                                href={`tel:${customer.mobilfunk}`}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Chip
                                  icon={<PhoneIcon />}
                                  label={`Mobil: ${customer.mobilfunk}`}
                                  size="small"
                                  clickable
                                  color="primary"
                                  variant="outlined"
                                />
                              </Link>
                            )}
                            {customer.festnetz && (
                              <Link
                                href={`tel:${customer.festnetz}`}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Chip
                                  icon={<PhoneIcon />}
                                  label={`Festnetz: ${customer.festnetz}`}
                                  size="small"
                                  clickable
                                  color="primary"
                                  variant="outlined"
                                />
                              </Link>
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(customer);
                          }}
                          sx={{ ml: 1 }}
                        >
                          <Edit />
                        </IconButton>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Paper>
      </Container>

      {/* Neuer Kunde Dialog */}
      <Dialog open={newCustomerDialog} onClose={() => setNewCustomerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Kunden anlegen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Kundennummer *"
              value={newCustomer.ku_nr}
              onChange={(e) => handleNewCustomerChange('ku_nr', e.target.value)}
              error={!!newCustomerErrors.ku_nr}
              helperText={newCustomerErrors.ku_nr || 'Genau 5 Ziffern erforderlich'}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 5 }}
            />
            <TextField
              fullWidth
              label="Name *"
              value={newCustomer.name}
              onChange={(e) => handleNewCustomerChange('name', e.target.value)}
              error={!!newCustomerErrors.name}
              helperText={newCustomerErrors.name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={newCustomer.email}
              onChange={(e) => handleNewCustomerChange('email', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Mobilfunk"
              value={newCustomer.mobilfunk}
              onChange={(e) => handleNewCustomerChange('mobilfunk', e.target.value)}
              onBlur={(e) => {
                if (e.target.value) {
                  setNewCustomer(prev => ({ 
                    ...prev, 
                    mobilfunk: formatPhoneNumber(e.target.value) 
                  }));
                }
              }}
              helperText="Führende 0 wird durch +49 ersetzt"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Festnetz"
              value={newCustomer.festnetz}
              onChange={(e) => handleNewCustomerChange('festnetz', e.target.value)}
              onBlur={(e) => {
                if (e.target.value) {
                  setNewCustomer(prev => ({ 
                    ...prev, 
                    festnetz: formatPhoneNumber(e.target.value) 
                  }));
                }
              }}
              helperText="Führende 0 wird durch +49 ersetzt"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Straße"
              value={newCustomer.strasse}
              onChange={(e) => handleNewCustomerChange('strasse', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={newCustomer.plz}
                  onChange={(e) => handleNewCustomerChange('plz', e.target.value)}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={newCustomer.ort}
                  onChange={(e) => handleNewCustomerChange('ort', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCustomerDialog(false)}>
            ABBRECHEN
          </Button>
          <Button onClick={handleCreateCustomer} variant="contained">
            KUNDE ANLEGEN
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bearbeiten Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kunde bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Kundennummer"
              value={editCustomer?.ku_nr || ''}
              disabled
              sx={{ mb: 2, bgcolor: '#f5f5f5' }}
            />
            <TextField
              fullWidth
              label="Name *"
              value={editCustomer?.name || ''}
              onChange={(e) => handleEditChange('name', e.target.value)}
              error={!!editErrors.name}
              helperText={editErrors.name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={editCustomer?.email || ''}
              onChange={(e) => handleEditChange('email', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Mobilfunk"
              value={editCustomer?.mobilfunk || ''}
              onChange={(e) => handleEditChange('mobilfunk', e.target.value)}
              onBlur={(e) => {
                if (e.target.value) {
                  setEditCustomer(prev => ({ 
                    ...prev, 
                    mobilfunk: formatPhoneNumber(e.target.value) 
                  }));
                }
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Festnetz"
              value={editCustomer?.festnetz || ''}
              onChange={(e) => handleEditChange('festnetz', e.target.value)}
              onBlur={(e) => {
                if (e.target.value) {
                  setEditCustomer(prev => ({ 
                    ...prev, 
                    festnetz: formatPhoneNumber(e.target.value) 
                  }));
                }
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Straße"
              value={editCustomer?.strasse || ''}
              onChange={(e) => handleEditChange('strasse', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={editCustomer?.plz || ''}
                  onChange={(e) => handleEditChange('plz', e.target.value)}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={editCustomer?.ort || ''}
                  onChange={(e) => handleEditChange('ort', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>
            ABBRECHEN
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            SPEICHERN
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
